/**
 * Deep Link Utilities
 *
 * Simple view deep linking system that enables any component to register handlers
 * for URL-based state restoration and provides URL generation for current states.
 */

import type { ViewParamsMap, ViewHandler, AnyViewHandler } from '@/types/viewParams.types';
import { isValidViewType, validateViewParams } from '@/types/viewParams.types';

// Type-safe registry for view handlers
const viewHandlers = new Map<keyof ViewParamsMap, AnyViewHandler>();

// Queue for deferred deep link processing when handlers aren't available yet
interface DeferredDeepLink {
  viewType: string;
  params: Record<string, string>;
}
const deferredDeepLinks: DeferredDeepLink[] = [];

/**
 * Register a view handler for a specific view type
 * @param viewType - The view type identifier (e.g., 'sharecontracts')
 * @param handler - The function to handle URL parameters for this view
 * @returns Cleanup function to unregister the handler
 */
export function registerViewHandler<T extends keyof ViewParamsMap>(viewType: T, handler: ViewHandler<T>): () => void {
  viewHandlers.set(viewType, handler);

  // Process any deferred deep links for this view type
  const deferredForThisType = deferredDeepLinks.filter((deferred) => deferred.viewType === viewType);
  if (deferredForThisType.length > 0) {
    console.log(`[DeepLink] Processing ${deferredForThisType.length} deferred deep link(s) for ${viewType}`);

    // Process each deferred deep link
    deferredForThisType.forEach((deferred) => {
      processDeepLink(deferred.viewType, deferred.params);
    });

    // Remove processed deferred deep links
    deferredDeepLinks.splice(0, deferredDeepLinks.length, ...deferredDeepLinks.filter((d) => d.viewType !== viewType));
  }

  // Return cleanup function for component unmount
  return () => {
    viewHandlers.delete(viewType);
  };
}

/**
 * Process deep link by finding and executing the appropriate view handler
 * @param viewType - The view type from URL parameters
 * @param rawParams - Raw query parameters from router
 */
export async function processDeepLink(viewType: string, rawParams: Record<string, string>): Promise<void> {
  // Validate view type
  if (!isValidViewType(viewType)) {
    console.warn(`[DeepLink] Unknown view type: ${viewType}`);
    return;
  }

  // Validate parameters
  const validation = validateViewParams(viewType, rawParams);
  if (!validation.isValid) {
    console.warn(`[DeepLink] Invalid parameters for ${viewType}:`, validation.errors);
    // Continue with valid parameters only
  }

  // Find and execute handler
  const handler = viewHandlers.get(viewType);
  if (handler) {
    try {
      await handler(validation.validatedParams);
    } catch (error) {
      console.error(`[DeepLink] Error in ${viewType} handler:`, error);
    }
  } else {
    console.warn(`[DeepLink] No handler registered for view type: ${viewType}. Deferring for later processing.`);

    // Queue this deep link for when the handler gets registered
    deferredDeepLinks.push({
      viewType,
      params: rawParams,
    });
  }
}

/**
 * Generate a deep link URL for a specific view and parameters
 * @param viewType - The view type identifier
 * @param params - Parameters for the view
 * @param baseRoute - Base route path (defaults to '/')
 * @returns Complete URL with query parameters
 */
export function generateViewUrl<T extends keyof ViewParamsMap>(
  viewType: T,
  params: Partial<ViewParamsMap[T]>,
  baseRoute = '/',
): string {
  const searchParams = new URLSearchParams();

  searchParams.set('view', viewType);

  // Convert params to string record for validation
  const stringParams: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      stringParams[key] = String(value);
    }
  });

  // Validate parameters
  const validation = validateViewParams(viewType, stringParams);
  if (validation.isValid) {
    Object.entries(validation.validatedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    });
  } else {
    console.warn(`[DeepLink] Invalid parameters for URL generation:`, validation.errors);
    Object.entries(validation.validatedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    });
  }

  // Construct final URL
  const queryString = searchParams.toString();
  return queryString ? `${baseRoute}?${queryString}` : baseRoute;
}

/**
 * Get currently registered view types
 * @returns Array of registered view type identifiers
 */
export function getRegisteredViewTypes(): (keyof ViewParamsMap)[] {
  return Array.from(viewHandlers.keys());
}

/**
 * Check if a view handler is registered for a specific view type
 * @param viewType - The view type to check
 * @returns True if handler is registered
 */
export function isViewHandlerRegistered(viewType: keyof ViewParamsMap): boolean {
  return viewHandlers.has(viewType);
}

/**
 * Clear all registered view handlers (useful for testing)
 */
export function clearAllViewHandlers(): void {
  viewHandlers.clear();
}

/**
 * Normalize deep link query parameters from `eh_*` prefixed params to internal names.
 * Marketing emails use `eh_view`, `eh_conid`, `eh_product_conid` but internal system
 * reads `view`, `conid`, `productConid`. Prefixed params take precedence.
 * @param params - Raw query parameters (string record)
 * @returns Normalized parameters with internal names
 */
export function normalizeDeepLinkQuery(params: Record<string, string>): Record<string, string> {
  const normalized: Record<string, string> = {};

  // eh_view → view (prefixed takes precedence)
  normalized.view = params.eh_view || params.view || '';

  // eh_conid → conid
  if (params.eh_conid || params.conid) {
    normalized.conid = params.eh_conid || params.conid;
  }

  // eh_product_conid → productConid
  if (params.eh_product_conid || params.productConid) {
    normalized.productConid = params.eh_product_conid || params.productConid;
  }

  // eh_show_share_dialog / show_share_dialog → showShareDialog
  if (params.eh_show_share_dialog || params.show_share_dialog) {
    normalized.showShareDialog = params.eh_show_share_dialog || params.show_share_dialog;
  }

  return normalized;
}

/**
 * Process deep link parameters from Vue router query object
 * Converts router query format to string record, normalizes `eh_*` prefixed params,
 * and processes the deep link.
 * @param query - Vue router query object
 */
export async function processRouterQuery(query: Record<string, string | string[]>): Promise<void> {
  // Convert query object to string record first
  const params: Record<string, string> = {};
  Object.entries(query).forEach(([key, value]) => {
    params[key] = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
  });

  // Normalize eh_* prefixed params to internal names
  const normalized = normalizeDeepLinkQuery(params);
  const viewType = normalized.view;

  if (!viewType) {
    return; // No deep linking requested
  }

  // Build handler params from normalized values (excluding view)
  const handlerParams: Record<string, string> = {};
  if (normalized.conid) handlerParams.conid = normalized.conid;
  if (normalized.productConid) handlerParams.productConid = normalized.productConid;

  // Process the deep link
  await processDeepLink(viewType, handlerParams);
}

/**
 * Deep link query parameter keys that should be stripped after processing.
 * Includes both internal names and eh_* prefixed variants from marketing emails.
 */
export const DEEP_LINK_PARAM_KEYS = [
  'view',
  'eh_view',
  'conid',
  'eh_conid',
  'productConid',
  'eh_product_conid',
  'show_share_dialog',
  'eh_show_share_dialog',
] as const;

/**
 * Strip deep link query parameters from a query object.
 * Used after modals close to clean up the URL and prevent re-triggering.
 * @param query - Current route query object
 * @returns New query object with deep link params removed, or null if no params to strip
 */
export function stripDeepLinkParams(
  query: Record<string, string | (string | null)[] | null | undefined>,
): Record<string, string | (string | null)[] | null | undefined> | null {
  const hasDeepLinkParams = DEEP_LINK_PARAM_KEYS.some((key) => key in query);
  if (!hasDeepLinkParams) return null;

  const cleaned: Record<string, string | (string | null)[] | null | undefined> = {};
  const keysToStrip = new Set<string>(DEEP_LINK_PARAM_KEYS);
  for (const [key, value] of Object.entries(query)) {
    if (!keysToStrip.has(key)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Generate shareable URL for current view state
 * Utility function that can be used by components to create shareable links
 * @param viewType - The view type identifier
 * @param currentState - Current state of the view
 * @param options - Additional options for URL generation
 */
export function generateShareableUrl<T extends keyof ViewParamsMap>(
  viewType: T,
  currentState: Partial<ViewParamsMap[T]>,
  options: {
    baseRoute?: string;
    includeHash?: boolean;
  } = {},
): string {
  const { baseRoute = '/', includeHash = true } = options;

  const url = generateViewUrl(viewType, currentState, baseRoute);

  // Add hash prefix for Vue Router hash mode if requested
  if (includeHash && !url.startsWith('#')) {
    return `#${url}`;
  }

  return url;
}

/**
 * Copy URL to clipboard with fallback for older browsers
 * @param url - URL to copy to clipboard
 * @returns Promise that resolves to true if successful
 */
export async function copyUrlToClipboard(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Modern clipboard API
      await navigator.clipboard.writeText(url);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      textArea.remove();
      return success;
    }
  } catch (error) {
    console.error('[DeepLink] Failed to copy URL to clipboard:', error);
    return false;
  }
}
